'use client';

import { AnimatePresence, LayoutGroup } from 'motion/react';
import * as m from 'motion/react-m';
import { usePathname } from 'next/navigation';
import { FC, useCallback, useEffect, useState } from 'react';

import { Link } from '@/general/components/Link/Link';

import { LazyMotionWrapperFull } from '../LazyMotionWrapper/LazyMotionWrapperFull';
import { InvestorHeaderMenuData, InvestorHeaderMenuItem } from './InvestorHeaderMenu.type';

type Props = {
  headerData: InvestorHeaderMenuData;
};

export const InvestorHeaderMenuLinks: FC<Props> = ({ headerData }) => {
  const pathname = usePathname();
  const [pathActiveIndex, setPathActiveIndex] = useState<number | null>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const handleSetActiveIndex = useCallback(
    (index: number | null) => {
      setActiveIndex(index ?? pathActiveIndex);
    },
    [setActiveIndex, pathActiveIndex],
  );

  useEffect(() => {
    let pathActiveIndex: number | null = null;
    if (pathname) {
      pathActiveIndex = headerData.navigation.findIndex((item) => {
        if (!item.href) {
          return false;
        }
        return pathname.startsWith(item.href);
      });
    }
    setPathActiveIndex(pathActiveIndex);
    setActiveIndex(pathActiveIndex);
  }, [pathname, headerData.navigation]);

  return (
    <LazyMotionWrapperFull>
      <LayoutGroup id="header-menu-link-underline">
        <ul className="hidden items-start justify-start mdp:flex">
          {headerData.navigation.map((item, index) => (
            <InvestorHeaderMenuLink
              item={item}
              index={index}
              key={item.label}
              activeIndex={activeIndex}
              setActiveIndex={handleSetActiveIndex}
            />
          ))}
        </ul>
      </LayoutGroup>
    </LazyMotionWrapperFull>
  );
};

type LinkProps = {
  index: number;
  item: InvestorHeaderMenuItem;
  activeIndex: number | null;
  setActiveIndex: (index: number | null) => void;
};

const InvestorHeaderMenuLink: FC<LinkProps> = ({ item, index, activeIndex, setActiveIndex }) => {
  const isActive = index === activeIndex;

  if (!item.href) {
    return null;
  }

  return (
    <li
      className="group/header-link relative"
      onMouseEnter={() => setActiveIndex(index)}
      onMouseLeave={() => setActiveIndex(null)}
    >
      <Link
        href={item.href}
        className="c-fs-body1 relative flex h-16 items-center justify-center overflow-hidden p-4 no-underline"
      >
        {item.label}
        <AnimatePresence>
          {isActive && (
            <m.div
              className="absolute top-0 left-1/2 h-2 w-12 -translate-1/2 rounded-full bg-current"
              layoutId="header-menu-link-underline"
              transition={{ duration: 0.2 }}
            />
          )}
        </AnimatePresence>
      </Link>
    </li>
  );
};
